// BRIEF 144 §4.2 — the bulk time-entry modal. BUILT BESIDE TimeEntryModal,
// not threaded through it, deliberately: the single modal's grammar is
// REQUIRED FIELDS (a date that defaults to today and can never be empty,
// minutes that must be > 0, a matter that must resolve, four "Save & …"
// footer variants, task-completion and timer coupling), and the bulk grammar
// is the exact inverse — EMPTY MEANS LEAVE UNCHANGED, without exception
// (ruling 2). Threading a `_bulk` flag through 800 lines whose header calls
// its own indentation a byte-proof would riddle the single path with
// ternaries; what Laurent asked for — "regular time entry modal, everything
// unfilled" — is the LOOK, and this file keeps it: same ModalLayout shell (`Mod` until 188 A.4), same field
// order (matter + collaborator, date + minutes, activity, billability,
// description), same field classes and label keys where the concept is the
// same.
//
// THE BILLABLE FLAG'S TRAP (§4.2) IS ANSWERED WITH FOUR STATES, NOT THREE:
// the tree's field is not a boolean but the trinary billabilityMark
// (unmarked | billable | unbillable, lib/billability.js), so "set billable /
// set non-billable / leave alone" becomes a four-option select whose default
// is leave-alone. A two-state checkbox here would silently un-bill every
// entry the user did not think about.
//
// Field state lives HERE, not in App's `form`: only the payload (_teIds)
// rides the om() form, so no stale App-form default can ever read as "set".
// The save calls the bulk route directly (src/api.ts bulkUpdateTimeEntries) —
// never the s() engine, whose diff would fan out to per-row PUTs and lose the
// route's per-row skip reporting. Skipped rows are NAMED in the result toast,
// grouped by reason (§4.3): invoiced, no authority, provenance collision.
//
// The collaborator picker is ADMIN-ONLY, mirroring C67 exactly as the single
// modal does: the server drops a non-admin's reassignment per row, so a
// non-admin is not shown a field whose value would be silently discarded.
/* 166 — `import React` is here for `react/react-in-jsx-scope`, which the
   committed eslint config still enforces and which the ratchet counts once per
   JSX element (`scripts/lint/eslint-ratchet.js`: a file's warning count may
   never rise). This brief adds JSX to this file, so the rule fires on every new
   element; putting React in scope is the rule's OWN remedy rather than a way
   around it, it costs nothing at runtime (the automatic JSX transform does not
   need it), and it clears the same debt for every element already here. */
import React from "react";
import { NO_AUTOFILL, noAutofillClass } from "../../lib/noAutofill"; // 213B §18 — no sign-ins, no contacts, in a modal's field
import { useState } from "react";
import { FS, RAD } from "../../lib/tokens";
import { resolveMatter } from "../../lib/matterLabel"; // 191 C.2b
import { ModalLayout } from "../SharedUI";
import MatterPicker from "../MatterPicker";
import type { MatterOption, PickedMatter } from "../MatterPicker";
import { fmin, pname } from "../../utils";
import { choosableCo } from "../../lib/collaboratorState";
import { roleLabel } from "../../hooks/useRoleLabels";
import { isDividerCode } from "../../utils/taxonomyMap";
import type { TFn } from "../../lib/taxonomyTypes";
import { bulkUpdateTimeEntries } from "../../api";
import type { TeBulkUpdateResult } from "../../api";
import MenuSelect from "../MenuSelect"; // 154B §2
import AmountField from "../AmountField"; // 208 §10 — the one amount field

type CoRow = { id: number; role?: string | null; status?: string | null; [k: string]: unknown };
type TkType = { code: string; desc?: string | null };

// 145 §1 — THE RESULT IS NOT THIS MODAL'S ANY MORE.
//
// It still performs the edit, because it owns the fields; what it no longer
// does is patch the entry list and write the toast. Both moved to
// TimeEntryTable's `bulkEditDone`, which is where the HOST's single-slot
// `confirmAndUndo` is reachable — the bulk delete's undo already lives on that
// slot, and the two buttons sit side by side in one bar. `_onDone` rides the
// om() payload beside `_teIds`; App passes it straight through as `onDone`.
//
// The skip-reason map, the row namer and the summary builder went with it.
// They were a second copy of TimeEntryTable's, keyed on the same server codes.
type Props = {
  form: { _teIds?: number[] };
  cm: () => void;
  t: TFn;
  toast: (m?: string, type?: string) => void;
  _appIsAdmin: boolean;
  collaborators: CoRow[];
  tkTypes: TkType[];
  cases: MatterOption[];
  collectiveCases: MatterOption[];
  getRecentCases: (u: unknown) => readonly unknown[];
  currentUser: number | null;
  // REQUIRED, and every one of the three bulk-bar hosts wires it. A save that
  // reaches a modal opened without it throws into the catch below and toasts —
  // which is the right failure for a wiring bug, and better than a silent
  // success that leaves the list showing pre-edit rows.
  onDone: (r: TeBulkUpdateResult) => void;
};

export default function BulkTimeEntryModal({
  form,
  cm,
  t,
  toast,
  _appIsAdmin,
  collaborators,
  tkTypes,
  cases,
  collectiveCases,
  getRecentCases,
  currentUser,
  onDone,
}: Props) {
  const ids = (form._teIds || []).map(Number).filter((n) => Number.isFinite(n) && n > 0);
  // Every field starts UNSET. "" / null is the leave-unchanged state and is
  // never sent; the patch carries only what the user filled in.
  const [caId, setCaId] = useState<number | null>(null);
  const [ccId, setCcId] = useState<number | null>(null);
  const [coId, setCoId] = useState("");
  const [date, setDate] = useState("");
  const [min, setMin] = useState("");
  const [actType, setActType] = useState("");
  const [mark, setMark] = useState("");
  const [desc, setDesc] = useState("");
  /* ── 166 §H.3 — THE STATED RATE, IN BULK ─────────────────────────────────
     `rate` follows the file's own "only what the user filled in" pattern
     (`!== ""` = the user filled it in). `rateClear` is the NEW shape and the
     brief asked for the measurement behind it: NO field in this modal can
     express "set to nothing" — all seven test `!== ""`, and the house's
     "— Laisser inchangé —" is precisely the absence of a value, so it cannot
     also mean a value of nothing. So an explicit tick, and on the wire an
     explicit `null` on `invoicedRate` (a value no control here produces on
     its own), which `timeEntries.js`'s bulk route carves out of its
     empty-means-unchanged strip for this one field. */
  const [rate, setRate] = useState("");
  const [rateClear, setRateClear] = useState(false);
  const [busy, setBusy] = useState(false);

  const teToday = new Date().toISOString().slice(0, 10);
  const future = date !== "" && date > teToday;
  const minN = min === "" ? null : Number(min);

  const doSave = () => {
    if (busy || future || !ids.length) return;
    /* 175 §1 — THE REFUSAL NOW NAMES THE RULE THE GUARD CHECKS. It read
       "a positive multiple of 5" while the only check here was `> 0`; the 5
       lived in the input's onChange, which rounded every keystroke, so three
       minutes was unreachable by construction and no validator ever enforced
       the number the message quoted. The rounding is gone (RULED 2026-09-09:
       rounding to a billing increment is the lawyer's judgment, not a text
       box's), so the guard states the whole rule — a whole minute above zero.
       `Number.isInteger` is the half that is new and it is load-bearing:
       `minutes` is an Int column and the server's `coerceIntFields`
       (`server/src/routes/timeEntries.js:148`) coerces without rounding, so a
       fractional minute would reach Prisma as a fraction. */
    if (minN != null && !(Number.isInteger(minN) && minN > 0)) {
      toast(t("te.bulk.min_invalid_v2", { fallback: "Minutes must be a whole number greater than zero" }));
      return;
    }
    // 166 §H.3 — the server refuses a bad rate rather than nulling it; refuse
    // it here too, so the user is told before ten rows are attempted.
    if (!rateClear && rate !== "" && !(Number.isFinite(Number(rate)) && Number(rate) >= 0)) {
      toast(t("te.bulk.rate_invalid", { fallback: "The hourly rate must be a number, or empty" }), "error");
      return;
    }
    const patch: Record<string, unknown> = {};
    if (caId != null) patch.caId = caId;
    else if (ccId != null) patch.ccId = ccId;
    if (_appIsAdmin && coId !== "") patch.coId = Number(coId);
    if (date !== "") patch.date = date;
    if (minN != null) patch.min = minN;
    if (actType !== "") patch.type = actType;
    if (mark !== "") patch.mark = mark;
    if (desc.trim() !== "") patch.desc = desc;
    // 166 §H.3 — the clear WINS over a typed value: a ticked box is an
    // unambiguous instruction and a number left in a disabled input is not.
    if (rateClear) patch.invoicedRate = null;
    else if (rate !== "") patch.invoicedRate = Number(rate);
    if (!Object.keys(patch).length) {
      toast(t("te.bulk.nothing_set", { fallback: "Nothing to change — every field is empty" }));
      return;
    }
    setBusy(true);
    bulkUpdateTimeEntries(ids, patch)
      .then((r) => {
        // Close FIRST: onDone opens a 30-second undo toast on the host's slot,
        // and it must not be raised behind a modal the user still has to
        // dismiss. A failure keeps the modal open, as it did.
        cm();
        onDone(r);
      })
      .catch((e: Error) => {
        toast(e.message, "error");
      })
      .finally(() => setBusy(false));
  };

  const pickedMatter = resolveMatter({ caseId: caId, ccId }, cases, collectiveCases); // 191 C.2b — its own expression, moved onto the one resolver
  const unchangedLabel = t("te.bulk.leave_unchanged", { fallback: "— Leave unchanged —" });

  return (
    <ModalLayout
      title={t("modal.title.bulk_edit_te", {
        count: ids.length,
        fallback: `Bulk edit ${ids.length} time entries`,
      })}
      onClose={cm}
      onSave={doSave}
    >
        <>
          <p style={{ fontSize: FS.s10, color: "var(--tm)", margin: "0 0 10px" }}>
            {t("te.bulk.empty_unchanged", {
              fallback: "Only the fields you fill in are applied — an empty field leaves the entries unchanged.",
            })}
          </p>
          <div className="field-row">
            <div className="field">
              <label>{t("field.case", { fallback: "Case" })}</label>
              <MatterPicker
                t={t}
                cases={cases}
                collectiveCases={collectiveCases}
                selectedId={caId != null ? caId : ccId}
                selectedKind={caId != null ? "ca" : ccId != null ? "cc" : null}
                placeholder={unchangedLabel}
                getRecentCases={getRecentCases}
                currentUser={currentUser}
                onClear={() => {
                  setCaId(null);
                  setCcId(null);
                }}
                onPick={(m: PickedMatter) => {
                  if (m._type === "cc") {
                    setCcId(m.id);
                    setCaId(null);
                  } else {
                    setCaId(m.id);
                    setCcId(null);
                  }
                }}
              />
              {pickedMatter && (
                <p style={{ fontSize: FS.s9_5, color: "var(--ts)", margin: "3px 0 0" }}>
                  {t("te.bulk.move_to", { fallback: "Entries move to" })}{" "}
                  <span style={{ fontWeight: 600 }}>{pickedMatter.title}</span>
                </p>
              )}
            </div>
            {_appIsAdmin && (
              <div className="field">
                <label>{t("field.collaborator", { fallback: "Collaborator" })}</label>
                <MenuSelect
                  className="field-input"
                  value={coId}
                  onChange={(v) => setCoId(v)}
                  options={[
                    ["", unchangedLabel] as const,
                    ...choosableCo(collaborators, null).map(
                      (c) =>
                        [
                          String(c.id),
                          `${pname(c)} (${roleLabel(c.role || "")})`,
                        ] as const,
                    ),
                  ]}
                />
              </div>
            )}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: 8 }}>
            <div className="field">
              <label>{t("common.date", { fallback: "Date" })}</label>
              <input
                className="field-input"
                type="date"
                max={teToday}
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
              {future && (
                <p style={{ fontSize: FS.s9_5, color: "var(--rd)", margin: "3px 0 0" }}>
                  {t("modal.te.cannot_log_future", { fallback: "Cannot log time for a future date" })}
                </p>
              )}
              {date !== "" && !future && (
                <button
                  type="button"
                  style={{
                    fontSize: FS.s9_5,
                    padding: "1px 6px",
                    marginTop: 4,
                    borderRadius: RAD.r12,
                    background: "none",
                    border: "1px solid var(--br)",
                    color: "var(--ts)",
                    cursor: "pointer",
                    fontFamily: "inherit",
                  }}
                  onClick={() => setDate("")}
                >
                  {unchangedLabel}
                </button>
              )}
            </div>
            <div className="field">
              <label>
                {t("field.minutes", { fallback: "Minutes" })}
                {minN != null && minN > 0 && (
                  <span style={{ fontWeight: 400, color: "var(--ts)", marginLeft: 6, textTransform: "none" }}>
                    = {fmin(minN)}
                  </span>
                )}
              </label>
              <input
                className="field-input"
                type="number"
                step={1}
                min={0}
                placeholder={unchangedLabel}
                value={min}
                /* 175 §1 — the typed value is stored as typed. A stepper that
                   jumps 5 while the field accepts 3 is the same lie one layer
                   down, so `step` is 1 as well. */
                onChange={(e) => setMin(e.target.value)}
              />
              {min !== "" && (
                <button
                  type="button"
                  style={{
                    fontSize: FS.s9_5,
                    padding: "1px 6px",
                    marginTop: 4,
                    borderRadius: RAD.r12,
                    background: "none",
                    border: "1px solid var(--br)",
                    color: "var(--ts)",
                    cursor: "pointer",
                    fontFamily: "inherit",
                  }}
                  onClick={() => setMin("")}
                >
                  {unchangedLabel}
                </button>
              )}
            </div>
          </div>
          {/* 166 §H.3 — beside the other "only if you fill it in" fields, with
              its own explicit CLEAR (ruling 8: clearable back to the derived
              rate). The value renders in `--st` while it holds one, so the box
              says the same thing the table's cell will (ruling 9). */}
          <div className="field">
            <label>{t("field.hourly_rate", { fallback: "Hourly rate (€/h)" })}</label>
            {/* 208 §10 — the one amount field (src/components/AmountField.tsx): typed with a decimal comma, shown « 1 234,50 » */}
            <AmountField
              inputMode="decimal"
              placeholder={unchangedLabel}
              value={rateClear ? null : rate}
              empty=""
              disabled={rateClear}
              style={!rateClear && rate !== "" ? { color: "var(--st)" } : undefined}
              onAmount={(n) => {
                setRate(n == null ? "" : String(n));
              }}
            />
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                fontSize: FS.s10,
                color: "var(--ts)",
                marginTop: 4,
                cursor: "pointer",
                textTransform: "none",
                fontWeight: 400,
              }}
            >
              <input
                type="checkbox"
                checked={rateClear}
                onChange={(e) => setRateClear(e.target.checked)}
              />
              {t("te.bulk.rate_clear", {
                fallback: "Clear the stated rate (back to the case's rate)",
              })}
            </label>
          </div>
          <div className="field">
            <label>{t("field.activity_type", { fallback: "Activity Type" })}</label>
            <MenuSelect
              className="field-input"
              value={actType}
              onChange={(v) => setActType(v)}
              options={[
                ["", unchangedLabel] as const,
                ...tkTypes.map(
                  (k) =>
                    [
                      String(k.code),
                      isDividerCode(k.code)
                        ? String(k.desc || "")
                        : `${k.code} — ${k.desc}`,
                    ] as const,
                ),
              ]}
            />
          </div>
          <div className="field">
            <label>{t("te.col.billable", { fallback: "Billability" })}</label>
            <MenuSelect
              className="field-input"
              value={mark}
              onChange={(v) => setMark(v)}
              options={[
                ["", unchangedLabel],
                ["unmarked", t("te.mark.unmarked", { fallback: "Mark" })],
                ["billable", t("te.mark.billable", { fallback: "Billable" })],
                ["unbillable", t("te.mark.unbillable", { fallback: "Unbillable" })],
              ]}
            />
          </div>
          <div className="field">
            <label>{t("field.description", { fallback: "Description" })}</label>
            <textarea
              {...NO_AUTOFILL}
              className={noAutofillClass("field-input")}
              rows={3}
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
              placeholder={unchangedLabel}
              style={{ resize: "vertical", fontFamily: "inherit", fontSize: FS.s11 }}
            />
          </div>
        </>
    </ModalLayout>
  );
}
